import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import {
  GetShotGunTemplate,
  PutShotGunTemplate,
  IShotgunTemplateGroup,
} from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";

import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import { ButtonNew as Button } from "components/buttonNew/index";

import "./shotgunTemplate.scss";

type ShotgunDirection = "regular" | "reverse";

interface IShotgunHoleRow extends Array<IShotgunTemplateGroup> {
  checked?: boolean;
  holeNumber?: number;
}

interface IEditState {
  groups: IShotgunTemplateGroup[];
  groupsFormatted: IShotgunHoleRow[] | null;
  direction: ShotgunDirection;
  deleteIds: Array<number>;
  teeTimeId: number;
  addTeeTimePopup: boolean;
  templateTitle: string;
  selectAllHoles: boolean;
  selectedHoles: number[];
  addHoleModalVisible: boolean;
  newHoleNumber: number;
}

function holeNumberFromStartHole(startHole: string): number {
  return parseInt(startHole, 10);
}

function copyHoleRow(row: IShotgunHoleRow, checked = !!row.checked): IShotgunHoleRow {
  const next = row.slice() as IShotgunHoleRow;
  next.holeNumber = row.holeNumber;
  next.checked = checked;
  return next;
}

function flattenHoleRows(rows: IShotgunHoleRow[]): IShotgunTemplateGroup[] {
  const groups: IShotgunTemplateGroup[] = [];
  rows.forEach(row => {
    row.forEach(group => {
      groups.push(group);
    });
  });
  return groups;
}

function formatGroupsInPositionOrder(teeTimes: IShotgunTemplateGroup[]): IShotgunHoleRow[] {
  const sorted = [...teeTimes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  const rows: IShotgunHoleRow[] = [];

  sorted.forEach(teeTime => {
    const holeNumber = holeNumberFromStartHole(teeTime.start_hole);
    const group: IShotgunTemplateGroup = {
      id: teeTime.id,
      group_index: holeNumber,
      start_hole: teeTime.start_hole,
      start_time: teeTime.start_time,
      position: teeTime.position,
    };
    const existingRow = rows[rows.length - 1];
    if (existingRow && existingRow.holeNumber === holeNumber) {
      existingRow.push(group);
      return;
    }

    const row: IShotgunHoleRow = [];
    row.holeNumber = holeNumber;
    row.checked = false;
    row.push(group);
    rows.push(row);
  });

  return rows;
}

function inferShotgunDirection(rows: IShotgunHoleRow[]): ShotgunDirection {
  const holes = rows.map(row => row.holeNumber).filter((hole): hole is number => typeof hole === "number");
  let descending = 0;
  let ascending = 0;

  for (let index = 1; index < holes.length; index++) {
    if (holes[index] < holes[index - 1]) {
      descending += 1;
    } else if (holes[index] > holes[index - 1]) {
      ascending += 1;
    }
  }

  return descending > ascending ? "reverse" : "regular";
}

function insertHoleRow(rows: IShotgunHoleRow[], row: IShotgunHoleRow, direction: ShotgunDirection): IShotgunHoleRow[] {
  const holeNumber = row.holeNumber;
  if (holeNumber == null) {
    return [...rows, row];
  }

  const index = rows.findIndex(existing => {
    if (existing.holeNumber == null) {
      return false;
    }

    return direction === "reverse" ? existing.holeNumber < holeNumber : existing.holeNumber > holeNumber;
  });

  if (index === -1) {
    return [...rows, row];
  }

  return [...rows.slice(0, index), row, ...rows.slice(index)];
}

export default function ShotGunTemplateEdit() {
  const { id } = useParams<any>();
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const facilityStore = useAppSelector(store => store.facilityStore);
  const { t } = useTranslation();

  const totalHoles = new Array(facilityStore?.facility?.holes);
  for (let i = 0; i < facilityStore?.facility?.holes; i++) {
    totalHoles[i] = i + 1;
  }

  const [editState, setEditState] = useState<IEditState>({
    groups: null,
    groupsFormatted: null,
    direction: "regular",
    deleteIds: [],
    teeTimeId: null,
    addTeeTimePopup: false,
    templateTitle: "",
    selectAllHoles: false,
    selectedHoles: [],
    addHoleModalVisible: false,
    newHoleNumber: null,
  });

  useEffect(() => {
    void loadShotgunTemplate();
  }, []);

  async function loadShotgunTemplate() {
    const templateRes = await GetShotGunTemplate({ id: Number(id), extended: true }, true);

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading shotgun template")));
      return;
    }

    const teeTimes = (templateRes.data[0].tee_times ?? []) as IShotgunTemplateGroup[];
    const rows = formatGroupsInPositionOrder(teeTimes);

    setEditState(prevState => ({
      ...prevState,
      groups: flattenHoleRows(rows),
      groupsFormatted: rows,
      direction: inferShotgunDirection(rows),
      templateTitle: templateRes.data[0].title,
      selectedHoles: [],
      selectAllHoles: false,
      deleteIds: [],
    }));
  }

  function handleDirectionChange(direction: ShotgunDirection) {
    setEditState(prevState => {
      if (prevState.direction === direction || !prevState.groupsFormatted) {
        return prevState;
      }

      const groupsFormatted = [...prevState.groupsFormatted].reverse().map(row => copyHoleRow(row, false));

      return {
        ...prevState,
        direction,
        groupsFormatted,
        groups: flattenHoleRows(groupsFormatted),
        selectedHoles: [],
        selectAllHoles: false,
      };
    });
  }

  async function updateShotgunTemplate() {
    const positionedGroups = flattenHoleRows(editState.groupsFormatted ?? [])
      .filter(group => !!group?.start_hole)
      .map((group, index) => ({
        ...group,
        position: index + 1,
      }));

    const templateRes = await PutShotGunTemplate(
      {
        template_id: Number(id),
        title: editState.templateTitle,
        groups: positionedGroups,
        delete_group_ids: editState.deleteIds.filter(groupId => !!groupId),
      },
      true,
    );

    if (templateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating shotgun template")));
      return;
    }

    dispatch(showSuccess(t("Shotgun template updated successfully")));
    void loadShotgunTemplate();
  }

  function addGroup(rowIndex: number) {
    const currentRows = editState.groupsFormatted;
    const row = currentRows?.[rowIndex];
    if (!currentRows || !row || row.length >= alphabet.length || row.length >= 4) {
      return;
    }

    const usedLetters = row.map(group => group.start_hole.substring(group.start_hole.length - 1));
    const currentGroupLetter = alphabet.find(letter => !usedLetters.includes(letter));
    if (!currentGroupLetter) {
      return;
    }

    const holeNumber = row.holeNumber ?? holeNumberFromStartHole(row[0].start_hole);
    const newGroup: IShotgunTemplateGroup = {
      id: 0,
      position: 0,
      group_index: holeNumber,
      start_hole: holeNumber.toString() + currentGroupLetter,
      start_time: "12:00",
    };

    const groupsFormatted = currentRows.map((existingRow, index) =>
      index === rowIndex ? copyHoleRow(existingRow) : existingRow,
    );
    groupsFormatted[rowIndex].push(newGroup);

    setEditState(prevState => ({
      ...prevState,
      groups: flattenHoleRows(groupsFormatted),
      groupsFormatted,
    }));
  }

  function removeGroup(rowIndex: number) {
    const currentRows = editState.groupsFormatted;
    const row = currentRows?.[rowIndex];
    if (!currentRows || !row || row.length === 0) {
      return;
    }

    const groupsFormatted = currentRows.map((existingRow, index) =>
      index === rowIndex ? copyHoleRow(existingRow) : existingRow,
    );
    const removedGroup = groupsFormatted[rowIndex].pop();
    const deleteIds = removedGroup?.id ? [...editState.deleteIds, removedGroup.id] : editState.deleteIds;

    setEditState(prevState => ({
      ...prevState,
      groups: flattenHoleRows(groupsFormatted),
      groupsFormatted,
      deleteIds,
    }));
  }

  function addHole() {
    const holeNumber = Number(editState.newHoleNumber);
    if (!holeNumber || holeNumber < 1) {
      return;
    }

    const currentRows = editState.groupsFormatted ?? [];
    const existingRowIndex = currentRows.findIndex(row => row.holeNumber === holeNumber);
    if (existingRowIndex >= 0 && currentRows[existingRowIndex].length > 0) {
      dispatch(showError(t("Hole already exists")));
      return;
    }

    const groupA: IShotgunTemplateGroup = {
      id: 0,
      position: 0,
      group_index: holeNumber,
      start_hole: holeNumber.toString() + "A",
      start_time: "12:00",
    };
    const groupB: IShotgunTemplateGroup = {
      id: 0,
      position: 0,
      group_index: holeNumber,
      start_hole: holeNumber.toString() + "B",
      start_time: "12:00",
    };
    const row: IShotgunHoleRow = [];
    row.holeNumber = holeNumber;
    row.checked = false;
    row.push(groupA);
    row.push(groupB);

    const groupsFormatted =
      existingRowIndex >= 0
        ? currentRows.map((existingRow, index) => (index === existingRowIndex ? row : existingRow))
        : insertHoleRow(currentRows, row, editState.direction);

    setEditState(prevState => ({
      ...prevState,
      groups: flattenHoleRows(groupsFormatted),
      groupsFormatted,
      addHoleModalVisible: false,
      newHoleNumber: null,
      selectedHoles: [],
      selectAllHoles: false,
    }));
  }

  function removeHole() {
    const groupsFormatted = (editState.groupsFormatted ?? []).map(row => copyHoleRow(row));
    const deleteIds = [...editState.deleteIds];

    editState.selectedHoles.forEach(rowIndex => {
      const row = groupsFormatted[rowIndex];
      if (!row) {
        return;
      }

      row.forEach(group => {
        if (group.id) {
          deleteIds.push(group.id);
        }
      });
      groupsFormatted[rowIndex] = [];
      groupsFormatted[rowIndex].holeNumber = row.holeNumber;
      groupsFormatted[rowIndex].checked = false;
    });

    setEditState(prevState => ({
      ...prevState,
      groups: flattenHoleRows(groupsFormatted),
      groupsFormatted,
      deleteIds,
      selectedHoles: [],
      selectAllHoles: false,
    }));
  }

  function handleShotgunInputChange(event: any) {
    const { id, value } = event.target;
    setEditState(prevState => ({ ...prevState, [id]: value }));
  }

  function selectAllHoles(event: any) {
    const checked = Boolean(event.target.checked);
    const groupsFormatted = (editState.groupsFormatted ?? []).map(row => copyHoleRow(row, checked && row.length > 0));
    const selectedHoles = checked
      ? groupsFormatted.map((row, index) => (row.length > 0 ? index : -1)).filter(index => index >= 0)
      : [];

    setEditState(prevState => ({
      ...prevState,
      selectAllHoles: checked,
      groupsFormatted,
      selectedHoles,
    }));
  }

  function handleHoleSelectChange(event: any, holeIndex: number) {
    const checked = Boolean(event.target.checked);
    const selectedHoles = checked
      ? [...editState.selectedHoles, holeIndex]
      : editState.selectedHoles.filter(index => index !== holeIndex);
    const groupsFormatted = (editState.groupsFormatted ?? []).map((row, index) =>
      index === holeIndex ? copyHoleRow(row, checked) : row,
    );

    setEditState(prevState => ({
      ...prevState,
      selectedHoles,
      groupsFormatted,
      selectAllHoles: groupsFormatted.some(row => row.length > 0) && groupsFormatted.every(row => row.length === 0 || row.checked),
    }));
  }

  const primaryAction = {
    content: t("Save"),
    action: updateShotgunTemplate,
  };

  return (
    <Page
      title={t("Edit Shotgun Template")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Shotgun Templates"),
          url: "/admin/settings/tee-sheet/shotgun-templates",
        },
      ]}
      primaryAction={primaryAction}
    >
      <Card>
        <Card.Section title={t("Information")}>
          <div className="flex justify-between gap-4">
            <div style={{ width: "70%" }}>
              <Input
                value={editState.templateTitle}
                label={t("Title")}
                id="templateTitle"
                onChange={handleShotgunInputChange}
              />
            </div>
            {editState.groupsFormatted && (
              <Select
                label={t("Direction")}
                onChange={handleDirectionChange}
                defaultValue={editState.direction}
              >
                <Option value="regular">
                  <span>{t("Regular")}</span>
                </Option>
                <Option value="reverse">
                  <span>{t("Reverse")}</span>
                </Option>
              </Select>
            )}
          </div>
        </Card.Section>
        <Card.Section title={t("Structure")}>
          <DataTable
            columns={[
              {
                label: "holes",
                width: "10%",
                content: (
                  <div className="flex">
                    <Checkbox size="medium" value={1} onChange={selectAllHoles} checked={editState.selectAllHoles} />
                    <span className="shotgun-template-edit-label">
                      {t("Holes")}
                    </span>
                  </div>
                ),
              },
              {
                label: t("Groups"),
              },
              {
                label: "delete",
                width: "15%",
                content: (
                  <div
                    title={t("Add group")}
                    onClick={removeHole}
                    className="shotgun-template_add_button"
                  >
                    Delete &nbsp; <FontAwesomeIcon icon={faMinus} />
                  </div>
                ),
              },
            ]}
            className="ui-table-clickable"
          >
            {editState?.groupsFormatted?.map((hole: IShotgunHoleRow, index: number) => {
              return (
                <tr key={`${hole.holeNumber ?? "hole"}-${index}`}>
                  <td>
                    <div className="flex items-center gap-4">
                      <div className="flex">
                        <Checkbox
                          size="medium"
                          value={1}
                          onChange={event => handleHoleSelectChange(event, index)}
                          checked={hole.checked}
                        />
                      </div>

                      <div className="shotgun-template_table_text">{hole.holeNumber ?? index + 1}</div>
                    </div>
                  </td>
                  <td>
                    <div className="shotgun-template_table_body_holes_container">
                      {hole.map((teeTime: any, teeTimeIndex: number) => {
                        return (
                          <div key={teeTimeIndex} className="shotgun-template_table_body_holes">
                            {teeTime.start_hole?.replace(/[0-9]/g, "")}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="shotgun-template_add_button">
                      <Button onClick={() => addGroup(index)} type="text" size="small" disabled={hole.length >= 4}>
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                      <Button onClick={() => removeGroup(index)} type="text" size="small" disabled={hole.length === 0}>
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </DataTable>
          {editState.groupsFormatted?.length < 18 && (
            <div
              title={t("Add hole")}
              className="shotgun-template_add_button mt-3"
              style={{ justifyContent: "left" }}
            >
              <Button
                onClick={() => setEditState(prevState => ({ ...prevState, addHoleModalVisible: true }))}
                type="text"
                size="small"
              >
                <FontAwesomeIcon icon={faPlus} /> &nbsp; Add hole
              </Button>
            </div>
          )}
        </Card.Section>
      </Card>

      <Sheet
        title={t("Add Hole")}
        open={editState.addHoleModalVisible}
        size="small"
        onOk={addHole}
        onCancel={() => setEditState(prevState => ({ ...prevState, addHoleModalVisible: false }))}
        okText={t("Add")}
        okDisabled={!editState.newHoleNumber}
      >
        <Input
          value={editState.newHoleNumber ?? ""}
          label={t("Hole Number")}
          id="newHoleNumber"
          onChange={handleShotgunInputChange}
          type={"number"}
        />
      </Sheet>
    </Page>
  );
}
